import {Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {BaseDialog} from "../../../basic-entity-front/basic-entity-table/base-dialog";
import {BaseDialogData} from "../../../basic-entity-front/basic-entity-table/base-dialog-data";
import {ClienteAsociado} from "../../../model/personas/cliente-asociado.model";
import {DireccionPersona} from "../../../model/personas/direccion-persona.model";
import {TelefonoPersona} from "../../../model/personas/telefono-persona.model";
import {InterfaceProviderService} from "../../../basic-entity-back/services/interface-provider.service";
import {ErrorDisplayService} from "../../../basic-entity-front/services/error-display.service";
import {SessionService} from "../../../session/session.service";
import {Cliente} from "../../../model/personas/cliente.model";
import {Pais} from "../../../model/localizacion/pais.model";
import {Provincia} from "../../../model/localizacion/provincia.model";
import {map} from "rxjs/operators";
import {Subject} from "rxjs";
import {validarDireccionFormGroup} from "../../personas/direccion-form/direccion-form.component";

/**
 * Alta y edición de una clínica asociada. No es un be-edition-dialog genérico: las direcciones y los
 * teléfonos se escriben anidados mandando siempre la colección completa (direccionesVigentes/
 * telefonos, ver cliente-asociado.model.ts), y ese manejo -añadir/editar/quitar localmente y guardar
 * todo junto- necesita su propio formulario, igual que cliente-edition.component.ts hace con los
 * suyos. A diferencia de las direcciones, los teléfonos no tienen histórico que preservar (CreateOnly
 * puro): editar uno lo modifica de verdad, no hace falta el filtrado vigentes/históricas.
 *
 * Extiende BaseDialog directamente (no EditionDialogComponent) para tener control total sobre ese
 * flujo; sigue siendo compatible con [editionDialog]/[creationDialog] de be-table porque
 * BaseDialog es el contrato que exige ese input.
 */
@Component({
    selector: 'app-asociado-edition',
    templateUrl: './asociado-edition.component.html',
    styleUrls: ['./asociado-edition.component.scss']
})
export class AsociadoEditionComponent extends BaseDialog<ClienteAsociado> {
    public formGroup: UntypedFormGroup;
    public tempAddressForm: UntypedFormGroup;
    public direcciones: DireccionPersona[];
    public addAddress = false;
    public tempAddress: DireccionPersona = null;
    public tempAddressId = -1;
    public tempPhoneForm: UntypedFormGroup;
    public telefonos: TelefonoPersona[];
    public addPhone = false;
    public tempPhone: TelefonoPersona = null;
    public tempPhoneId = -1;
    public saving = false;

    /**
     * Emite la clínica tal y como la devuelve la API al guardarla -con los IRI ya asignados a ella
     * y a sus direcciones-, justo antes de cerrar el diálogo. Lo usa quien abre este diálogo desde
     * fuera del listado de clínicas (la edición de una línea de pedido) para quedarse con la
     * dirección recién creada sin tener que releerla. `be-table` no lo mira: le basta el
     * RESULT_SHOULD_RELOAD de siempre.
     */
    public readonly guardado$ = new Subject<ClienteAsociado>();

    public paises: Pais[] = [];
    public provincias: Provincia[] = [];

    constructor(@Inject(MAT_DIALOG_DATA) data: BaseDialogData<ClienteAsociado>,
                dialogRef: MatDialogRef<AsociadoEditionComponent>,
                private _intf: InterfaceProviderService,
                private _session: SessionService,
                private fb: UntypedFormBuilder,
                private _errorService: ErrorDisplayService) {
        super(data, dialogRef);

        if (this.isNew) {
            // La API acota sola quién puede colgar un asociado de qué cliente (OwnedBy sobre
            // 'cliente'); aquí sólo hace falta mandar el IRI del cliente de la sesión.
            const sesion = this._session.profile as Cliente;
            this.model.cliente = sesion?.iri;
        }

        // Copia de trabajo de las direcciones vigentes: añadir/editar/borrar aquí no toca la API
        // hasta que se pulsa "Guardar" (ver guardar()), que manda la colección completa anidada.
        this.direcciones = [...(this.model.direccionesVigentes || [])];

        // Mismo mecanismo que las direcciones, pero sin históricas: telefonos ya es la lista completa
        // (no hay una 'telefonosVigentes' derivada, ver cliente-asociado.model.ts).
        this.telefonos = [...(this.model.telefonos || [])];

        this.formGroup = this.fb.group({
            denominacionSocial: [this.model.denominacionSocial, Validators.required],
            cifNif: [this.model.cifNif],
            email: [this.model.email, Validators.email]
        });
        this.formGroup.valueChanges.subscribe(() => this.modified = true);

        this.tempAddressForm = new UntypedFormGroup({
            id: new UntypedFormControl(0),
            calle: new UntypedFormControl(''),
            tipoDireccion: new UntypedFormControl(DireccionPersona.TIPOS_DIRECCION.ENVIO),
            poblacion: new UntypedFormControl(''),
            demarcacion: new UntypedFormControl(null),
            cp: new UntypedFormControl(''),
            pais: new UntypedFormControl(null)
        }, {validators: [validarDireccionFormGroup]});

        // Mismos campos y validaciones que cliente-edition.component.ts (tempPhoneForm): el teléfono
        // es obligatorio y de 9 cifras. codigoArea también es obligatorio -TelefonoSuperclass lo
        // exige con Assert\NotBlank en la API-: por defecto trae '+34', pero al editar se puede
        // vaciar y sin el validador el guardado se iba en un 422 sin explicación en el formulario.
        // Sin control 'id': a diferencia de cliente-edition, aquí el guardado edita directamente el
        // objeto referenciado en tempPhone (ver guardarTelefonoLocal()), así que no hace falta pasar
        // el id por el formulario para volver a leerlo luego.
        this.tempPhoneForm = new UntypedFormGroup({
            codigoArea: new UntypedFormControl('+34', [Validators.required, Validators.maxLength(3)]),
            telefono: new UntypedFormControl('', [Validators.required, Validators.minLength(9), Validators.maxLength(9)]),
            extension: new UntypedFormControl(''),
            departamento: new UntypedFormControl('')
        });

        this._intf.managerForModel(Provincia).loader.findAndFollow().pipe(map(page => page.member)).subscribe(provincias => {
            this.provincias.push(...provincias);
        });
        this._intf.managerForModel(Pais).loader.findAndFollow([]).pipe(map(page => page.member)).subscribe(paises => {
            this.paises.push(...paises);
        });
    }

    get title(): string {
        return this.isNew ? 'Nueva clínica asociada' : ('Clínica asociada - ' + (this.model.denominacionSocial || ''));
    }

    nombrePais(direccion: DireccionPersona): string {
        return direccion.pais ? (this.paises.find(p => p.iri.toString() === direccion.pais.toString())?.nombreDefault ?? '') : '';
    }

    nombreProvincia(direccion: DireccionPersona): string {
        return direccion.demarcacion ? (this.provincias.find(p => p.iri.toString() === direccion.demarcacion.toString())?.nombre ?? '') : '';
    }

    nuevaDireccion() {
        this.tempAddressId = -1;
        this.tempAddress = null;
        this.tempAddressForm.reset();
        this.tempAddressForm.get('tipoDireccion').setValue(DireccionPersona.TIPOS_DIRECCION.ENVIO);
        this.addAddress = true;
    }

    editarDireccionLocal(direccion: DireccionPersona, index: number) {
        this.tempAddressId = index;
        this.tempAddress = direccion;
        this.addAddress = true;
        this.tempAddressForm.get('id').setValue(direccion.id ?? 0);
        this.tempAddressForm.get('calle').setValue(direccion.calle);
        this.tempAddressForm.get('tipoDireccion').setValue(direccion.tipoDireccion || DireccionPersona.TIPOS_DIRECCION.ENVIO);
        this.tempAddressForm.get('cp').setValue(direccion.cp);
        this.tempAddressForm.get('poblacion').setValue(direccion.poblacion);
        this.tempAddressForm.get('demarcacion').setValue(direccion.demarcacion);
        this.tempAddressForm.get('pais').setValue(direccion.pais);
    }

    cancelarDireccion() {
        this.addAddress = false;
        this.tempAddressId = -1;
        this.tempAddress = null;
        this.tempAddressForm.reset();
    }

    guardarDireccionLocal() {
        if (this.tempAddressId === -1) {
            const nueva = new DireccionPersona();
            nueva.calle = this.tempAddressForm.get('calle').value;
            nueva.tipoDireccion = this.tempAddressForm.get('tipoDireccion').value;
            nueva.cp = this.tempAddressForm.get('cp').value;
            nueva.poblacion = this.tempAddressForm.get('poblacion').value;
            nueva.demarcacion = this.tempAddressForm.get('demarcacion').value;
            nueva.pais = this.tempAddressForm.get('pais').value;
            this.direcciones.push(nueva);
        } else {
            this.tempAddress.calle = this.tempAddressForm.get('calle').value;
            this.tempAddress.tipoDireccion = this.tempAddressForm.get('tipoDireccion').value;
            this.tempAddress.cp = this.tempAddressForm.get('cp').value;
            this.tempAddress.poblacion = this.tempAddressForm.get('poblacion').value;
            this.tempAddress.demarcacion = this.tempAddressForm.get('demarcacion').value;
            this.tempAddress.pais = this.tempAddressForm.get('pais').value;
            this.direcciones.splice(this.tempAddressId, 1, this.tempAddress);
        }
        this.modified = true;
        this.addAddress = false;
        this.tempAddressId = -1;
        this.tempAddress = null;
        this.tempAddressForm.reset();
    }

    borrarDireccionLocal(index: number) {
        this.direcciones.splice(index, 1);
        this.modified = true;
    }

    nuevoTelefono() {
        this.tempPhoneId = -1;
        this.tempPhone = null;
        this.tempPhoneForm.reset();
        // reset() deja los controles a null (no al valor inicial del constructor, ver
        // nuevaDireccion() más arriba con tipoDireccion), así que el código de país por defecto hay
        // que reponerlo a mano en cada alta, no sólo en la primera.
        this.tempPhoneForm.get('codigoArea').setValue('+34');
        this.addPhone = true;
    }

    editarTelefonoLocal(telefono: TelefonoPersona, index: number) {
        this.tempPhoneId = index;
        this.tempPhone = telefono;
        this.addPhone = true;
        this.tempPhoneForm.get('codigoArea').setValue(telefono.codigoArea);
        this.tempPhoneForm.get('telefono').setValue(telefono.telefono);
        this.tempPhoneForm.get('extension').setValue(telefono.extension);
        this.tempPhoneForm.get('departamento').setValue(telefono.departamento);
    }

    cancelarTelefono() {
        this.addPhone = false;
        this.tempPhoneId = -1;
        this.tempPhone = null;
        this.tempPhoneForm.reset();
    }

    guardarTelefonoLocal() {
        if (this.tempPhoneId === -1) {
            const nuevo = new TelefonoPersona();
            nuevo.codigoArea = this.tempPhoneForm.get('codigoArea').value;
            nuevo.telefono = this.tempPhoneForm.get('telefono').value;
            nuevo.extension = this.tempPhoneForm.get('extension').value;
            nuevo.departamento = this.tempPhoneForm.get('departamento').value;
            this.telefonos.push(nuevo);
        } else {
            this.tempPhone.codigoArea = this.tempPhoneForm.get('codigoArea').value;
            this.tempPhone.telefono = this.tempPhoneForm.get('telefono').value;
            this.tempPhone.extension = this.tempPhoneForm.get('extension').value;
            this.tempPhone.departamento = this.tempPhoneForm.get('departamento').value;
            this.telefonos.splice(this.tempPhoneId, 1, this.tempPhone);
        }
        this.modified = true;
        this.addPhone = false;
        this.tempPhoneId = -1;
        this.tempPhone = null;
        this.tempPhoneForm.reset();
    }

    borrarTelefonoLocal(index: number) {
        this.telefonos.splice(index, 1);
        this.modified = true;
    }

    guardar() {
        if (this.formGroup.invalid) {
            return;
        }
        // Una dirección o un teléfono a medio escribir cuentan: quien los rellena y pulsa "Guardar"
        // sin pasar por "Añadir a la lista" espera que se guarden. Antes se perdían sin aviso, y la
        // clínica quedaba sin dirección y fuera del desplegable de dirección de entrega del pedido.
        if (this.addAddress) {
            if (this.tempAddressForm.invalid) {
                this.tempAddressForm.markAllAsTouched();
                this._errorService.displayRaw('Completa la dirección o cancélala antes de guardar.');
                return;
            }
            this.guardarDireccionLocal();
        }
        if (this.addPhone) {
            if (this.tempPhoneForm.invalid) {
                this.tempPhoneForm.markAllAsTouched();
                this._errorService.displayRaw('Completa el teléfono o cancélalo antes de guardar.');
                return;
            }
            this.guardarTelefonoLocal();
        }
        // La API lo exige igual (ClienteAsociado::validarDireccionEnvio); aquí se avisa sin ir y volver.
        if (!this.direcciones.some(d => d.tipoDireccion === DireccionPersona.TIPOS_DIRECCION.ENVIO && !d.historico)) {
            this._errorService.displayRaw('Añade al menos una dirección de envío: es la que se ofrece al hacer un pedido.');
            return;
        }
        this.model.denominacionSocial = this.formGroup.get('denominacionSocial').value;
        this.model.cifNif = this.formGroup.get('cifNif').value;
        this.model.email = this.formGroup.get('email').value;
        // Colección completa anidada (históricas + vigentes): direccionesVigentes se encarga de
        // conservar las históricas, ver cliente-asociado.model.ts.
        this.model.direccionesVigentes = this.direcciones;
        // Sin histórico que preservar (a diferencia de direcciones): telefonos es directamente la
        // colección completa.
        this.model.telefonos = this.telefonos;

        const manager = this._intf.managerForModel(ClienteAsociado);
        this.saving = true;
        const obs = this.isNew ? manager.add(this.model) : manager.update(this.model);
        obs.subscribe(
            guardado => {
                this.saving = false;
                // Las direcciones del desplegable de entrega del pedido salen de las clínicas del
                // perfil de sesión (/me): sin refrescarlo, la clínica nueva -o la dirección editada,
                // que la API guarda con otro id- no aparecía hasta recargar la página. Aquí y no en
                // quien abre el diálogo, para que valga igual desde "Mis clínicas" y desde el pedido.
                this._session.refreshProfile();
                this.guardado$.next(guardado);
                this.closeDialog(true);
            },
            err => {
                this.saving = false;
                this._errorService.displayError(err);
            }
        );
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.guardado$.complete();
    }
}
